import { DocumentReference } from "fhir/r5";
import dayjs from "dayjs";

/**
 * Utilitários para processamento de documentos FHIR
 * Centraliza lógica comum de filtragem, extração e transformação de dados
 */

/**
 * Verifica se um documento está assinado (tem ProofOfOrigin)
 */
export function isDocumentSigned(resource: any): boolean {
  return (
    resource?.securityLabel?.[0]?.coding?.[0]?.code === "ProofOfOrigin"
  );
}

/**
 * Filtra documentos baseado no status de assinatura
 * @param entries - Array de entradas FHIR
 * @param sign - Se true, retorna apenas documentos assinados; se false, apenas não assinados
 */
export function filterBySignStatus(
  entries: any[],
  sign: boolean,
): any[] {
  if (!entries) return [];

  return entries.filter((e: any) => {
    const isSigned = isDocumentSigned(e?.resource);
    return sign ? isSigned : !isSigned && e?.resource?.resourceType === "DocumentReference";
  });
}

/**
 * Extrai informações do practitioner de um recurso contained
 */
export function extractPractitionerInfo(resource: any): {
  name: string;
  identifier: string;
} {
  const practitioner = resource?.contained?.find(
    (contained: any) => contained?.resourceType === "Practitioner",
  );

  return {
    name: practitioner?.name?.[0]?.given?.[0] || "",
    identifier: practitioner?.identifier?.[0]?.value || "",
  };
}

/**
 * Extrai a especialidade do practiceSetting
 */
export function extractSpecialty(resource: any): string {
  return resource?.practiceSetting?.coding?.[0]?.display || "-";
}

/**
 * Extrai a URL do anexo binário do documento
 */
export function extractBinaryUrl(resource: any): string {
  return resource?.content?.[0]?.attachment?.url || "";
}

/**
 * Formata data usando dayjs
 */
export function formatDocumentDate(date: string | undefined): string {
  return date ? dayjs(date).format("DD/MM/YYYY") : "-";
}

/**
 * Limpa e junta expressões, removendo duplicatas e valores vazios
 */
export function cleanAndJoin(
  expressions: any[],
  delimiter: string = ", ",
): string {
  return Array.from(
    new Set(expressions.filter((item: any) => item && item.trim() !== "")),
  ).join(delimiter);
}

/**
 * Traduz categorias de inglês para português
 */
export function translateCategory(category: string): string {
  const translations: Record<string, string> = {
    "Laboratory procedure": "Exame Laboratorial",
    Imaging: "Exame de imagem",
  };

  return translations[category] || category;
}

/**
 * Busca recursos relacionados baseado em referências basedOn
 */
export function findRelatedResources(
  documentResource: any,
  relatedResources: any[],
): any[] {
  if (!relatedResources || !documentResource?.basedOn) return [];

  return relatedResources.filter((request: any) => {
    const reqId = request?.resource?.id ?? request?.id;
    if (!reqId) return false;

    return documentResource.basedOn?.some((basedOn: any) =>
      basedOn?.reference?.includes(reqId),
    );
  });
}

export function findDocumentReferenceForMedication(
  medicationRequestId: string | undefined,
  documentReferences: any[],
): any | undefined {
  if (!medicationRequestId || !documentReferences?.length) return undefined;

  return documentReferences.find((entry: any) => {
    const resource = entry?.resource ?? entry;
    if (resource?.resourceType !== "DocumentReference") return false;

    return resource.basedOn?.some((basedOn: any) =>
      basedOn?.reference?.includes(medicationRequestId),
    );
  });
}

/**
 * Extrai URLs de resultados de recursos relacionados
 */
export function extractResultUrls(results: any[]): string[] {
  if (!results) return [];

  return results
    .map(
      (result: any) => result?.resource?.content?.[0]?.attachment?.url || "",
    )
    .filter((url: string) => url !== "");
}

/**
 * Extrai nome da organização do recurso contained
 */
export function extractOrganizationName(resource: any): string | null {
  const fromContained = resource?.contained?.find(
    (contained: any) => contained?.resourceType === "Organization",
  );
  const containedName = fromContained?.name || fromContained?.alias?.[0];
  if (containedName?.trim()) return containedName.trim();

  const authorOrg = resource?.author?.find((author: any) =>
    author?.reference?.includes("Organization"),
  );
  if (authorOrg?.display?.trim()) return authorOrg.display.trim();

  if (resource?.custodian?.display?.trim()) {
    return resource.custodian.display.trim();
  }

  return null;
}

export interface PosologyLine {
  name: string;
  posology: string;
}

/** Formata posologia a partir de um MedicationRequest */
export function formatMedicationPosology(medicationEntry: any): PosologyLine {
  const resource = medicationEntry?.resource ?? medicationEntry;
  const name = resource?.medication?.concept?.text?.trim() || "Medicamento";
  const dosage = resource?.dosageInstruction?.[0];

  if (dosage?.text?.trim()) {
    return { name, posology: dosage.text.trim() };
  }

  const auxQtd = dosage?.doseAndRate?.[0]?.doseQuantity;
  const auxType = dosage?.doseAndRate?.[0]?.type?.text;
  const auxTiming = dosage?.timing?.repeat;
  const auxDuration = auxTiming?.duration;
  const auxPeriod = auxTiming?.period;

  const dosePart =
    auxQtd?.unit && auxQtd?.value != null && auxType
      ? `${auxQtd.value} ${auxQtd.unit}`
      : "";

  const timingPart = auxPeriod
    ? `a cada ${auxPeriod} h${auxDuration
      ? ` durante ${auxDuration} dia(s)`
      : " por tempo indeterminado"
    }`
    : "";

  const posology =
    [dosePart, timingPart].filter(Boolean).join(" · ") ||
    "Posologia não informada";

  return { name, posology };
}

export function extractPosologyLines(relatedMedications: any[]): PosologyLine[] {
  if (!relatedMedications?.length) return [];
  return relatedMedications.map(formatMedicationPosology);
}

/**
 * Processa dados básicos de um documento para exibição
 */
export function processDocumentBaseData(
  entry: fhir5.BundleEntry<DocumentReference | any>,
  sign: boolean,
): {
  fullResource: any;
  practitioner: string;
  date: string;
  specialty: string;
  sign: boolean;
  binaryUrl: string;
} {
  const { name, identifier } = extractPractitionerInfo(entry?.resource);
  const practitionerLabel = name ? `Dr(a). ${name}` : identifier;

  return {
    fullResource: entry?.resource,
    practitioner: practitionerLabel,
    date: formatDocumentDate(entry?.resource?.date),
    specialty: extractSpecialty(entry?.resource),
    sign,
    binaryUrl: extractBinaryUrl(entry?.resource),
  };
}
